/**
 * <recharge-cart-widget>
 *
 * Custom subscription selector for cart line items. Markup is server-rendered by
 * snippets/recharge-cart-widget.liquid; this element only wires interaction:
 *   - a custom ARIA listbox (button trigger + option list) replacing a native <select>
 *   - on select, switches the line item's selling plan (or removes it for "Buy Once")
 *     by posting to /cart/change.js, mirroring the theme's quantity-change flow.
 *
 * The widget is stateless: after a successful change the cart re-renders (drawer) or
 * reloads (cart page), so a fresh server-rendered widget reflects the new state.
 */
(function () {
  if (window.customElements.get('recharge-cart-widget')) return;

  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  class RechargeCartWidget extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      const { signal } = this.controller;

      this.trigger = this.querySelector('.rc-cart-widget__trigger');
      this.list = this.querySelector('.rc-cart-widget__list');
      this.options = Array.from(this.querySelectorAll('.rc-cart-widget__option'));
      this.busy = false;
      this.activeIndex = -1;

      // Keyboard-activated clicks report detail === 0; let onTriggerKeydown own those
      // so Enter/Space open with keyboard focus rather than as a mouse-style open.
      this.trigger.addEventListener('click', (e) => { if (e.detail !== 0) this.toggle(false); }, { signal });
      this.trigger.addEventListener('keydown', (e) => this.onTriggerKeydown(e), { signal });
      this.list.addEventListener('keydown', (e) => this.onListKeydown(e), { signal });

      this.options.forEach((option) => {
        option.addEventListener('click', () => this.select(option), { signal });
        // Mouse highlight is pure CSS (:hover); clear any keyboard-active row so the
        // two highlight sources never show at once.
        option.addEventListener('mousemove', () => this.clearActive(), { signal });
      });

      // Close on outside click / focus loss.
      document.addEventListener('click', this.onDocumentClick.bind(this), { signal });
      // Close this widget whenever another one opens (one open at a time).
      document.addEventListener('rc-cart-widget:open', this.onOtherOpen.bind(this), { signal });
    }

    disconnectedCallback() {
      this.controller.abort();
    }

    get isOpen() {
      return this.trigger.getAttribute('aria-expanded') === 'true';
    }

    toggle(viaKeyboard) {
      this.isOpen ? this.close() : this.open(viaKeyboard);
    }

    open(viaKeyboard, startIndex = 0) {
      if (this.busy) return;
      if (!this.isOpen) {
        document.dispatchEvent(new CustomEvent('rc-cart-widget:open', { detail: { source: this } }));
        this.trigger.setAttribute('aria-expanded', 'true');
        this.classList.add('is-open');
        this.list.hidden = false;
      }
      if (viaKeyboard) {
        // Listbox holds focus; the active option is tracked via aria-activedescendant
        // and highlighted with .is-active (no DOM focus moves to options).
        this.list.focus();
        this.setActiveIndex(startIndex);
      }
    }

    close({ focusTrigger = false } = {}) {
      if (!this.isOpen) return;
      this.trigger.setAttribute('aria-expanded', 'false');
      this.classList.remove('is-open');
      this.list.hidden = true;
      this.clearActive();
      if (focusTrigger) this.trigger.focus();
    }

    setActiveIndex(index) {
      this.activeIndex = index;
      this.options.forEach((option, i) => {
        const active = i === index;
        option.classList.toggle('is-active', active);
        option.setAttribute('aria-selected', active ? 'true' : 'false');
      });
      const option = this.options[index];
      if (option) {
        this.list.setAttribute('aria-activedescendant', option.id);
        option.scrollIntoView({ block: 'nearest' });
      }
    }

    clearActive() {
      if (this.activeIndex === -1) return;
      this.activeIndex = -1;
      this.options.forEach((option) => {
        option.classList.remove('is-active');
        option.setAttribute('aria-selected', 'false');
      });
      this.list.removeAttribute('aria-activedescendant');
    }

    onOtherOpen(event) {
      if (event.detail.source !== this) this.close();
    }

    onDocumentClick(event) {
      if (this.isOpen && !this.contains(event.target)) this.close();
    }

    onTriggerKeydown(event) {
      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          this.open(true, 0);
          break;
        case 'ArrowUp':
          event.preventDefault();
          this.open(true, this.options.length - 1);
          break;
        case 'Enter':
        case ' ':
          event.preventDefault();
          this.toggle(true);
          break;
        case 'Escape':
          if (this.isOpen) {
            event.preventDefault();
            this.close();
          }
          break;
      }
    }

    onListKeydown(event) {
      const last = this.options.length - 1;

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          this.setActiveIndex(this.activeIndex >= last ? 0 : this.activeIndex + 1);
          break;
        case 'ArrowUp':
          event.preventDefault();
          this.setActiveIndex(this.activeIndex <= 0 ? last : this.activeIndex - 1);
          break;
        case 'Home':
          event.preventDefault();
          this.setActiveIndex(0);
          break;
        case 'End':
          event.preventDefault();
          this.setActiveIndex(last);
          break;
        case 'Enter':
        case ' ':
          event.preventDefault();
          if (this.options[this.activeIndex]) this.select(this.options[this.activeIndex]);
          break;
        case 'Escape':
          event.preventDefault();
          this.close({ focusTrigger: true });
          break;
        case 'Tab':
          this.close();
          break;
      }
    }

    select(option) {
      const sellingPlanId = option.getAttribute('data-selling-plan-id');
      const current = this.getAttribute('data-selected-plan') || '';

      // No-op if re-selecting the active plan (shouldn't appear in list, but guard anyway).
      if (sellingPlanId === current) {
        this.close({ focusTrigger: true });
        return;
      }

      this.close();
      this.changePlan(sellingPlanId);
    }

    setBusy(state) {
      this.busy = state;
      this.classList.toggle('rc-cart-widget--busy', state);
      this.trigger.disabled = state;
      this.setAttribute('aria-busy', state ? 'true' : 'false');
    }

    showError(message) {
      const container = this.closest('line-item')?.querySelector('.line-item__error-container');
      if (!container) return;
      container.innerHTML = '';
      container.insertAdjacentHTML(
        'afterbegin',
        `<p class="h-stack gap-1.5 text-sm text-error" role="alert">${message}</p>`
      );
    }

    // Mirrors the theme's changeLineItemQuantity flow (assets/theme.js), swapping
    // quantity for selling_plan so quantity is preserved.
    async changePlan(sellingPlanId) {
      const lineKey = this.getAttribute('data-line-key');
      this.setBusy(true);

      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

      const lineItem = this.closest('line-item');
      lineItem?.dispatchEvent(new CustomEvent('line-item:will-change', { bubbles: true, detail: {} }));

      const sectionsToBundle = new Set();
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
      );

      let response;
      try {
        response = await fetch(`${root}cart/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: lineKey,
            selling_plan: sellingPlanId === '' ? null : sellingPlanId,
            sections: [...sectionsToBundle].join(',')
          })
        });
      } catch (error) {
        response = null;
      }

      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));

      if (!response || !response.ok) {
        const content = response ? await response.json().catch(() => ({})) : {};
        this.showError(content.description || 'Could not update your subscription. Please try again.');
        this.setBusy(false);
        return;
      }

      const cart = await response.json();

      // Cart page reloads (matches the theme's quantity handler); drawer re-renders via events.
      if (window.themeVariables?.settings?.pageType === 'cart') {
        window.location.reload();
        return;
      }

      const after = cart.items.filter((item) => item.key === lineKey);
      lineItem?.dispatchEvent(
        new CustomEvent('line-item:change', {
          bubbles: true,
          detail: { quantity: after.length === 0 ? 0 : after[0].quantity, cart }
        })
      );
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'line-item:change', cart } })
      );
    }
  }

  window.customElements.define('recharge-cart-widget', RechargeCartWidget);
})();
